import { useState } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import { uploadToStorage } from '../../../config/supabase';
import { REQUEST_FIELDS, RISK_LEVELS, fullName } from '../utils/blacklistService';

const GROUPS = ['Personnel', 'Identification', 'Contact', 'Adresse', 'Incident'];

/**
 * Agency blacklist submission form. Props: value, onChange, onSubmit, onCancel, busy.
 */
export default function BlacklistSubmitForm({ value, onChange, onSubmit, onCancel, busy }) {
  const { isDark } = useTheme();
  const [uploading, setUploading] = useState(false);

  const cardBg = isDark ? 'bg-gray-800' : 'bg-white';
  const inputCls = `w-full px-2.5 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`;
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const txt = isDark ? 'text-white' : 'text-gray-900';

  const set = (k, v) => onChange({ ...value, [k]: v });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const atts = [...(value.attachments || [])];
    for (const f of files) {
      try { atts.push({ name: f.name, url: await uploadToStorage(f, 'blacklist_requests'), type: f.type }); }
      catch (_) {}
    }
    onChange({ ...value, attachments: atts });
    setUploading(false);
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px' }}>
      <div className={cardBg} style={{ borderRadius: '16px', width: '100%', maxWidth: '760px', maxHeight: '92vh', overflowY: 'auto', padding: '20px' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-black text-lg ${txt}`}>🚩 {value.id ? 'Modifier la demande' : 'Signaler un client (liste noire)'}</h3>
          <button onClick={onCancel} className={`text-2xl ${sub}`}>×</button>
        </div>
        <p className={`text-xs mb-3 ${sub}`}>
          La demande sera envoyée au Super Admin pour approbation. Elle n'apparaît dans la liste noire globale qu'après validation.
        </p>

        {GROUPS.map(group => (
          <div key={group} className="mb-3">
            <div className={`text-[11px] font-black uppercase tracking-wide mb-1 ${sub}`}>{group}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {REQUEST_FIELDS.filter(f => f.group === group).map(f => {
                if (f.key === 'riskLevel') return (
                  <div key={f.key}>
                    <label className={`text-[10px] font-bold ${sub}`}>{f.label}</label>
                    <select value={value.riskLevel} onChange={e => set('riskLevel', e.target.value)} className={inputCls}>
                      {RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                );
                const isWide = ['address', 'incident', 'reason', 'notes'].includes(f.key);
                return (
                  <div key={f.key} className={isWide ? 'sm:col-span-2' : ''}>
                    <label className={`text-[10px] font-bold ${sub}`}>{f.label}{f.required ? ' *' : ''}</label>
                    {f.key === 'incident' || f.key === 'notes'
                      ? <textarea rows={2} value={value[f.key] || ''} onChange={e => set(f.key, e.target.value)} className={inputCls} />
                      : <input type={f.type || 'text'} value={value[f.key] || ''} onChange={e => set(f.key, e.target.value)} className={inputCls} />}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Attachments */}
        <div className="mb-3">
          <div className={`text-[11px] font-black uppercase tracking-wide mb-1 ${sub}`}>📎 Documents (PDF, images, vidéos, rapport de police)</div>
          <input type="file" multiple accept=".pdf,image/*,video/*,.doc,.docx" onChange={onFiles} className={`text-xs ${txt}`} />
          {uploading && <span className="text-xs text-amber-500 ml-2">⏳ Upload…</span>}
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {(value.attachments || []).map((a, i) => (
              <span key={i} className={`text-[11px] rounded-lg px-2 py-0.5 ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">{a.name}</a>
                <button onClick={() => onChange({ ...value, attachments: value.attachments.filter((_, j) => j !== i) })} className="text-red-500 ml-1">×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={onCancel} className={`flex-1 py-2.5 rounded-xl font-bold text-sm ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>Annuler</button>
          <button onClick={onSubmit} disabled={busy || uploading} className="flex-1 py-2.5 rounded-xl font-bold text-sm bg-red-600 hover:bg-red-700 text-white disabled:opacity-60">
            {busy ? '⏳' : (value.id ? '💾 Renvoyer' : '📤 Envoyer la demande')}
          </button>
        </div>
      </div>
    </div>
  );
}
